import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Field, Input, useToast } from '@w7/ui'
import { useState, type FormEvent } from 'react'
import { cmApi, type SecurityBlockedIpRow } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { enabledChip } from './securityUi'
import { SecurityCrudPage } from './SecurityCrudPage'

export function BlockedIpPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const [ip, setIp] = useState('')
  const [comment, setComment] = useState('')

  const saveM = useMutation({
    mutationFn: () =>
      cmApi.securityBlockedIpSave(authHeaders(), {
        ip: ip.trim(),
        comment: comment.trim() || undefined,
      }),
    onSuccess: async () => {
      toast.success('Blocked IP saved')
      setIp('')
      setComment('')
      await qc.invalidateQueries({ queryKey: ['cm', 'security', 'blocked-ip'] })
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : String(err)),
  })

  return (
    <SecurityCrudPage<SecurityBlockedIpRow>
      title="Blocked IP"
      subtitle="Block player IPs from logging in"
      queryKey="blocked-ip"
      emptyHint="No blocked IPs yet."
      listFn={cmApi.securityBlockedIpList}
      deleteFn={cmApi.securityBlockedIpDelete}
      columns={[
        { key: 'ip', header: 'IP', cell: (r) => r.ip },
        { key: 'comment', header: 'Comment', cell: (r) => r.comment ?? '—' },
        { key: 'enabled', header: 'Enabled', cell: (r) => enabledChip(r.enabled) },
        { key: 'by', header: 'By', cell: (r) => r.createdBy ?? '—' },
        {
          key: 'when',
          header: 'Updated',
          cell: (r) => new Date(r.updatedAt).toLocaleString(),
        },
      ]}
      form={
        <form
          className="cm-form"
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            saveM.mutate()
          }}
        >
          <div className="cm-form-grid">
            <Field label="IP address" required>
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  required
                  value={ip}
                  onChange={(e) => setIp(e.target.value)}
                  placeholder="IP address"
                />
              )}
            </Field>
            <Field label="Comment">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Optional"
                />
              )}
            </Field>
            <div className="cm-form-actions">
              <Button type="submit" busy={saveM.isPending}>
                Add / update
              </Button>
            </div>
          </div>
        </form>
      }
    />
  )
}
